import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { Damage, DamageAmount, DamageEvent, DamageUserRef } from '../../../interfaces/Damages.interfaces';
import { Parcels } from '../../../interfaces/Parcels.interfaces';
import { Waybill } from '../../../interfaces/Waybills.interfaces';
import { Brand, Dealer } from '../../../interfaces/waybill.interfaces';
import { Carriers } from '../../../interfaces/Carriers.interfaces';
import { OprClient } from '../../../interfaces/anagraphic.interfaces';
import { DamageManagementService, DamageEventsPage } from '../../../services/damage-management.service';
import { FunctionService } from '../../../services/function.service';
import { NotificationService } from '../../../services/notification.service';
import {
  DAMAGE_OPERATIONS,
  DamageOperation,
  DamageOperationMeta,
  DamageOperationPopupComponent
} from './damage-operation-popup/damage-operation-popup.component';
import { DamageImagesComponent } from './damage-images/damage-images.component';
import { DamageRoutingComponent } from './damage-routing/damage-routing.component';
import { DamageNavBoxComponent } from '../damage-overview/damage-nav-box/damage-nav-box.component';

/** Riquadro della sezione "Operazioni": metadati dell'operazione più disponibilità */
interface DamageOperationTile extends DamageOperationMeta {
  enabled: boolean;
  /** Motivo per cui il riquadro è disabilitato (tooltip) */
  disabledReason: string | null;
}

/** Totale per tipo di importo, come restituito da GET /damaged-parcels/{id} (esclusi i CANCELLED) */
interface DamageTotal {
  type: string;
  value: number;
}

/**
 * Gestione pratica danno: dettaglio di una pratica raggiunto da
 * /damage-overview/detail/{damageId} (bottone "Gestisci Pratica Danno" della
 * tabella colli di /damaged-parcels). Legge testata, cronologia eventi, importi e
 * totali da GET /damaged-parcels/{damageId}. Stile allineato a /damaged-parcels e
 * /damaged-parcels/create (header con icona, strumentario, card con separatore
 * tratteggiato, metric card, dmg-badge, dmg-table).
 */
@Component({
  selector: 'app-damage-detail',
  standalone: true,
  imports: [CommonModule, TranslateModule, DamageOperationPopupComponent, DamageImagesComponent, DamageRoutingComponent, DamageNavBoxComponent],
  templateUrl: './damage-detail.component.html',
  styleUrls: ['./damage-detail.component.css']
})
export class DamageDetailComponent implements OnInit, OnDestroy {
  damage: Damage | null = null;
  events: DamageEvent[] = [];
  amounts: DamageAmount[] = [];
  totals: DamageTotal[] = [];
  // Anagrafiche collegate risolte dal backend (null se il codice non è stato trovato)
  parcel: Parcels | null = null;
  waybill: Waybill | null = null;
  brand: Brand | null = null;
  client: OprClient | null = null;
  dealer: Dealer | null = null;
  carrier: Carriers | null = null;
  /** Utenti citati dalla pratica, per userCode */
  users: Record<string, DamageUserRef> = {};
  isLoading = true;
  /** Messaggio dell'errore di caricamento (vuoto se tutto ok) */
  errorMessage = '';
  /** True se il backend ha risposto 404: la pratica non esiste */
  notFound = false;
  damageId = 0;

  // ─── OPERAZIONI (sezione "Operazioni": ogni riquadro apre il popup dell'operazione) ───
  /** Operazione aperta nel popup, null se chiuso */
  activeOperation: DamageOperation | null = null;
  /** Importo su cui opera il popup (modifica/eliminazione dalla tabella importi) */
  activeAmount: DamageAmount | null = null;

  /** Numero di immagini della sezione "Immagini" (dal componente figlio), null finché non caricato */
  imagesCount: number | null = null;
  /** Numero di tappe della sezione "Instradamento" (dal componente figlio), null finché non caricato */
  routingCount: number | null = null;

  /** True mentre il backend genera il PDF della pratica (bottone dello strumentario disabilitato) */
  isPrintingPdf = false;

  // ─── CRONOLOGIA PAGINATA (GET /{id}/events) ───
  /** Eventi della pagina corrente della cronologia */
  timelineEvents: DamageEvent[] = [];
  /** Numero totale di eventi della pratica (dal backend) */
  timelineTotal = 0;
  /** Pagina corrente, 1-based */
  timelinePage = 1;
  /** Eventi per pagina, scelto dall'utente (ricordato nel browser) */
  timelinePageSize = 10;
  readonly timelinePageSizeOptions = [5, 10, 25, 50];
  /** Ordine dal più recente (default come la pagina: dal più vecchio) */
  timelineNewestFirst = false;
  /** Vista compatta (tabella) invece della timeline estesa (ricordata nel browser) */
  timelineCompact = false;
  isTimelineLoading = false;
  timelineError = '';
  private static readonly TIMELINE_PREFS_KEY = 'damage-timeline-prefs';

  private routeSub?: Subscription;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private damageService: DamageManagementService,
    private functionService: FunctionService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    // Si arriva dalla tabella di /damaged-parcels, spesso scorsa in basso: il router
    // non ripristina lo scroll, quindi la pagina va riportata in cima.
    window.scrollTo({ top: 0, behavior: 'auto' });
    this.restoreTimelinePrefs();

    this.routeSub = this.route.paramMap.subscribe(params => {
      this.damageId = Number(params.get('id')) || 0;
      this.timelinePage = 1;
      this.loadDamageDetail();
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    if (this.copiedTimeout) clearTimeout(this.copiedTimeout);
  }

  /**
   * Carica (o ricarica) il dettaglio della pratica dal backend. Con silent
   * (dopo un'operazione) non mostra lo scheletro di caricamento: la pagina
   * resta a video e si aggiorna all'arrivo dei dati.
   */
  loadDamageDetail(silent: boolean = false): void {
    if (!silent) this.isLoading = true;
    this.errorMessage = '';
    this.notFound     = false;

    if (!this.damageId) {
      this.isLoading = false;
      this.notFound  = true;
      return;
    }

    this.damageService.getDamageDetail(this.damageId).pipe(
      finalize(() => { this.isLoading = false; })
    ).subscribe({
      next: detail => {
        this.damage  = detail.damage;
        this.events  = detail.events;
        this.amounts = detail.amounts;
        this.totals  = Object.entries(detail.totals ?? {})
          .map(([type, value]) => ({ type, value: Number(value) || 0 }))
          .sort((a, b) => a.type.localeCompare(b.type));
        this.parcel  = detail.parcel;
        this.waybill = detail.waybill;
        this.brand   = detail.brand;
        this.client  = detail.client;
        this.dealer  = detail.dealer;
        this.carrier = detail.carrier;
        this.users   = detail.users ?? {};
        if (!this.damage) this.notFound = true;
        // La cronologia a video è paginata lato backend: la carica a parte
        if (this.damage) this.loadTimeline();
      },
      error: (err: Error) => {
        this.damage  = null;
        this.events  = [];
        this.amounts = [];
        this.totals  = [];
        this.parcel  = null;
        this.waybill = null;
        this.brand   = null;
        this.client  = null;
        this.dealer  = null;
        this.carrier = null;
        this.users   = {};
        if (err?.name === 'NotFound') {
          this.notFound = true;
        } else {
          this.errorMessage = err?.message || 'Errore nel caricamento della pratica. Riprova più tardi.';
        }
      }
    });
  }

  // ─── NOMI AL POSTO DEI CODICI ───

  /** Nome dell'utente dal suo codice; il codice stesso se non risolto */
  userName(userCode?: string | null): string | null {
    if (!userCode) return null;
    const ref = this.users[userCode] ?? this.users[userCode.trim()];
    return ref?.userName?.trim() || userCode;
  }

  /** Email dell'utente dal suo codice, se risolto */
  userEmail(userCode?: string | null): string | null {
    if (!userCode) return null;
    return (this.users[userCode] ?? this.users[userCode.trim()])?.userEmail || null;
  }

  /** Il codice come nota secondaria del campo, solo se il nome è stato risolto ed è diverso */
  codeNote(name: string | null | undefined, code: string | null | undefined): string | null {
    if (!code || !name || name === code) return null;
    return code;
  }

  get clientName(): string | null {
    return this.client?.clientName?.trim() || this.damage?.damageClientCode || null;
  }

  get brandName(): string | null {
    return this.brand?.brandName?.trim() || this.damage?.damageBrandCode || null;
  }

  get dealerName(): string | null {
    return this.dealer?.dealerName?.trim() || this.dealer?.dealerLegalName?.trim()
      || this.damage?.damageDealerCode || null;
  }

  get carrierName(): string | null {
    return this.carrier?.carrierName?.trim() || this.waybill?.waybillCarrierName?.trim()
      || this.damage?.damageCarrierCode || null;
  }

  /** Indirizzo operativo del dealer su una riga (via numero, CAP città (provincia)) */
  get dealerAddress(): string | null {
    const d = this.dealer;
    if (!d) return null;
    const street = [d.dealerOperatingAddressStreet, d.dealerOperatingAddressNumber].filter(Boolean).join(' ');
    const city   = [d.dealerOperatingAddressZip, d.dealerOperatingAddressCity].filter(Boolean).join(' ');
    const prov   = d.dealerOperatingAddressProvince ? `(${d.dealerOperatingAddressProvince})` : '';
    const line   = [street, [city, prov].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    return line || null;
  }

  // ─── STATO DELLA PRATICA ───

  /** True se la pratica è in uno stato finale (CLOSED o REJECTED) */
  get isClosed(): boolean {
    const status = this.damage?.damageStatus ?? '';
    return status === 'CLOSED' || status === 'REJECTED';
  }

  /** True se la pratica è ancora aperta e la scadenza è passata */
  get isOverdue(): boolean {
    if (this.isClosed || !this.damage?.damageDueDate) return false;
    const due = new Date(this.damage.damageDueDate);
    return !isNaN(due.getTime()) && due.getTime() < Date.now();
  }

  /** Classe dmg-badge per lo stato della pratica */
  get statusBadgeClass(): string {
    switch (this.damage?.damageStatus) {
      case 'OPEN':        return 'dmg-badge--info';
      case 'IN_PROGRESS': return 'dmg-badge--pending';
      case 'WAITING':     return 'dmg-badge--medium';
      case 'CLOSED':      return 'dmg-badge--resolved';
      case 'REJECTED':    return 'dmg-badge--critical';
      default:            return 'dmg-badge--info';
    }
  }

  /** Classe colore dell'icona della metric card "Stato pratica" */
  get statusIconClass(): string {
    switch (this.damage?.damageStatus) {
      case 'CLOSED':      return 'dmg-metric-icon--green';
      case 'REJECTED':    return 'dmg-metric-icon--red';
      case 'IN_PROGRESS': return 'dmg-metric-icon--yellow';
      case 'WAITING':     return 'dmg-metric-icon--orange';
      default:            return 'dmg-metric-icon--blue';
    }
  }

  /** Classe dmg-badge per la gravità */
  get severityBadgeClass(): string {
    switch (this.damage?.damageSeverity) {
      case 'CRITICAL': return 'dmg-badge--critical';
      case 'HIGH':     return 'dmg-badge--high';
      case 'MEDIUM':   return 'dmg-badge--medium';
      case 'LOW':      return 'dmg-badge--low';
      default:         return 'dmg-badge--info';
    }
  }

  /** Valuta della pratica: quella del primo importo, EUR in mancanza */
  get currency(): string {
    return this.amounts.find(a => !!a.damageAmountCurrency)?.damageAmountCurrency ?? 'EUR';
  }

  // ─── COPIA NEGLI APPUNTI (come le celle di /damaged-parcels) ───

  /** Chiave del campo appena copiato: il suo bottone mostra la spunta */
  copiedKey: string | null = null;
  private copiedTimeout: any = null;

  /** True se il campo è quello appena copiato */
  isCopied(key: string): boolean {
    return this.copiedKey === key;
  }

  /** Copia negli appunti il valore del campo, con spunta di conferma per 1,5s */
  copyValue(key: string, value: unknown): void {
    const text = value == null ? '' : String(value).trim();
    if (!text) return;
    this.writeToClipboard(text).then(ok => {
      if (!ok) return;
      this.copiedKey = key;
      if (this.copiedTimeout) clearTimeout(this.copiedTimeout);
      this.copiedTimeout = setTimeout(() => this.copiedKey = null, 1500);
    });
  }

  private writeToClipboard(text: string): Promise<boolean> {
    if (navigator.clipboard?.writeText) {
      return navigator.clipboard.writeText(text).then(() => true).catch(() => this.legacyCopy(text));
    }
    return Promise.resolve(this.legacyCopy(text));
  }

  /** Fallback per contesti senza Clipboard API (es. origine http non sicura) */
  private legacyCopy(text: string): boolean {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    document.body.removeChild(textarea);
    return ok;
  }

  // ─── AZIONI ───

  backToList(): void {
    this.router.navigate(['/damage-overview']);
  }

  printReport(): void {
    window.print();
  }

  /**
   * Scarica il PDF della scheda pratica generato dal backend
   * (GET /damaged-parcels/{id}/print, template damages-template): stessa
   * logica di download delle bolle (blob, link temporaneo, revoca dell'URL).
   */
  downloadReportPdf(): void {
    if (!this.damage || this.isPrintingPdf) return;

    this.isPrintingPdf = true;
    const code = (this.damage.damageCode || String(this.damage.damageId)).replace(/[^A-Za-z0-9_-]/g, '_');

    this.damageService.printDamageReport(this.damage.damageId).pipe(
      finalize(() => { this.isPrintingPdf = false; })
    ).subscribe({
      next: (blob: Blob) => this.downloadBlob(blob, `Pratica_Danno_${code}_${this.nowStamp()}.pdf`),
      error: (err: Error) => {
        this.notificationService.error(`Errore nella generazione del PDF: ${err?.message || 'riprova più tardi'}`);
      }
    });
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  private nowStamp(): string {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${yyyy}${mm}${dd}_${hh}${mi}`;
  }

  // ─── OPERAZIONI ───

  /** True se l'utente ha il permesso UPDATE sulla funzione damages (stessa funzione dei permessi di /damaged-parcels) */
  get canOperate(): boolean {
    return this.functionService.canUpdate('damages');
  }

  /**
   * Riquadri della sezione "Operazioni". Su pratica chiusa restano
   * disponibili solo nota e importi; "Stato importo" richiede almeno un importo.
   */
  get operationTiles(): DamageOperationTile[] {
    return DAMAGE_OPERATIONS.filter(op => !op.rowOnly).map(op => {
      let disabledReason: string | null = null;
      if (op.requiresOpen && this.isClosed) {
        disabledReason = 'Non disponibile: la pratica è chiusa';
      } else if (op.key === 'amountStatus' && this.amounts.length === 0) {
        disabledReason = 'Non disponibile: nessun importo registrato';
      }
      return { ...op, enabled: !disabledReason, disabledReason };
    });
  }

  /** Riquadro dell'operazione se disponibile e l'utente può operare, altrimenti null (il bottone non compare) */
  operationTile(operation: DamageOperation): DamageOperationTile | null {
    if (!this.canOperate) return null;
    const tile = this.operationTiles.find(t => t.key === operation);
    return tile?.enabled ? tile : null;
  }

  get isOperationPopupVisible(): boolean {
    return this.activeOperation !== null;
  }

  openOperation(operation: DamageOperation): void {
    if (!this.canOperate) return;
    const tile = this.operationTiles.find(t => t.key === operation);
    if (!tile?.enabled) return;
    this.activeAmount = null;
    this.activeOperation = operation;
  }

  /** Apre il popup di stato, modifica o eliminazione di un importo dalla riga della tabella, con l'importo già selezionato */
  openAmountOperation(operation: 'amountStatus' | 'amountEdit' | 'amountDelete', amount: DamageAmount): void {
    if (!this.canOperate || !amount) return;
    this.activeAmount = amount;
    this.activeOperation = operation;
  }

  closeOperation(): void {
    this.activeOperation = null;
    this.activeAmount = null;
  }

  /** Operazione completata: chiude il popup e ricarica il dettaglio in silenzio */
  onOperationDone(): void {
    this.activeOperation = null;
    this.activeAmount = null;
    this.loadDamageDetail(true);
  }

  trackByOperation(index: number, tile: DamageOperationTile): string {
    return tile.key;
  }

  // ─── CRONOLOGIA ───

  /**
   * Carica la pagina corrente della cronologia dal backend (GET /{id}/events)
   * con il numero di elementi per pagina scelto. Gli utenti citati dagli eventi
   * si aggiungono a quelli del dettaglio, così i nomi restano risolti.
   */
  loadTimeline(): void {
    if (!this.damageId) return;
    this.isTimelineLoading = true;
    this.timelineError = '';
    this.damageService.getDamageEvents(this.damageId, this.timelinePage, this.timelinePageSize, this.timelineNewestFirst).pipe(
      finalize(() => { this.isTimelineLoading = false; })
    ).subscribe({
      next: (page: DamageEventsPage) => {
        this.timelineEvents = page.events;
        this.timelineTotal  = page.total;
        this.users = { ...this.users, ...page.users };
        // Pagina oltre la fine (es. dopo un'eliminazione): torna all'ultima disponibile
        if (page.totalPages > 0 && this.timelinePage > page.totalPages) {
          this.timelinePage = page.totalPages;
          this.loadTimeline();
        }
      },
      error: (err: Error) => {
        this.timelineEvents = [];
        this.timelineTotal  = 0;
        this.timelineError  = err?.message || 'Errore nel caricamento della cronologia. Riprova più tardi.';
      }
    });
  }

  get timelineTotalPages(): number {
    return Math.max(1, Math.ceil(this.timelineTotal / this.timelinePageSize));
  }

  get timelineRangeStart(): number {
    return this.timelineTotal === 0 ? 0 : (this.timelinePage - 1) * this.timelinePageSize + 1;
  }

  get timelineRangeEnd(): number {
    return Math.min(this.timelinePage * this.timelinePageSize, this.timelineTotal);
  }

  /** Numero progressivo dell'evento nella cronologia completa (per la vista compatta) */
  timelineIndex(index: number): number {
    const offset = (this.timelinePage - 1) * this.timelinePageSize;
    return this.timelineNewestFirst ? this.timelineTotal - offset - index : offset + index + 1;
  }

  onTimelinePageChange(page: number): void {
    if (page < 1 || page > this.timelineTotalPages || page === this.timelinePage) return;
    this.timelinePage = page;
    this.loadTimeline();
  }

  setTimelinePageSize(size: number): void {
    if (size === this.timelinePageSize) return;
    // Mantiene a video il primo elemento della pagina corrente
    const firstItem = (this.timelinePage - 1) * this.timelinePageSize;
    this.timelinePageSize = size;
    this.timelinePage = Math.floor(firstItem / size) + 1;
    this.saveTimelinePrefs();
    this.loadTimeline();
  }

  setTimelineCompact(compact: boolean): void {
    if (compact === this.timelineCompact) return;
    this.timelineCompact = compact;
    this.saveTimelinePrefs();
  }

  toggleTimelineOrder(): void {
    this.timelineNewestFirst = !this.timelineNewestFirst;
    this.timelinePage = 1;
    this.saveTimelinePrefs();
    this.loadTimeline();
  }

  /** Numeri di pagina della barra: finestra di 5 attorno alla corrente, estremi e puntini (null) */
  getTimelinePageItems(): (number | null)[] {
    const total = this.timelineTotalPages;
    const current = this.timelinePage;
    const maxButtons = 5;
    let start = Math.max(1, current - Math.floor(maxButtons / 2));
    const end = Math.min(total, start + maxButtons - 1);
    if (end - start + 1 < maxButtons) start = Math.max(1, end - maxButtons + 1);
    const items: (number | null)[] = [];
    if (start > 1) {
      items.push(1);
      if (start > 2) items.push(null);
    }
    for (let i = start; i <= end; i++) items.push(i);
    if (end < total) {
      if (end < total - 1) items.push(null);
      items.push(total);
    }
    return items;
  }

  trackByPageItem(index: number, item: number | null): string {
    return item === null ? `gap-${index}` : `p-${item}`;
  }

  /** Preferenze di visualizzazione della cronologia (vista, elementi per pagina, ordine) nel browser */
  private restoreTimelinePrefs(): void {
    try {
      const raw = localStorage.getItem(DamageDetailComponent.TIMELINE_PREFS_KEY);
      if (!raw) return;
      const prefs = JSON.parse(raw);
      if (typeof prefs.compact === 'boolean') this.timelineCompact = prefs.compact;
      if (this.timelinePageSizeOptions.includes(Number(prefs.pageSize))) this.timelinePageSize = Number(prefs.pageSize);
      if (typeof prefs.newestFirst === 'boolean') this.timelineNewestFirst = prefs.newestFirst;
    } catch {
      // Preferenze non leggibili: restano i default
    }
  }

  private saveTimelinePrefs(): void {
    try {
      localStorage.setItem(DamageDetailComponent.TIMELINE_PREFS_KEY, JSON.stringify({
        compact: this.timelineCompact,
        pageSize: this.timelinePageSize,
        newestFirst: this.timelineNewestFirst
      }));
    } catch {
      // Storage non disponibile: la preferenza vale solo per la sessione
    }
  }

  /** Icona Font Awesome per il tipo di evento della cronologia */
  eventIcon(type?: string): string {
    switch (type) {
      case 'OPENED':                return 'fa-folder-open';
      case 'STATUS_CHANGED':        return 'fa-exchange-alt';
      case 'CLASSIFICATION_CHANGED': return 'fa-layer-group';
      case 'ASSIGNED':              return 'fa-user-check';
      case 'NOTE_ADDED':            return 'fa-sticky-note';
      case 'AMOUNT_ADDED':          return 'fa-euro-sign';
      case 'AMOUNT_STATUS_CHANGED': return 'fa-file-invoice-dollar';
      case 'AMOUNT_UPDATED':        return 'fa-pen';
      case 'AMOUNT_DELETED':        return 'fa-trash-alt';
      case 'CLOSED':                return 'fa-check-circle';
      default:                      return 'fa-circle';
    }
  }

  /** Variante colore dell'icona per il tipo di evento (stesse tinte delle metric card) */
  eventClass(type?: string): string {
    switch (type) {
      case 'OPENED':                return 'dmg-metric-icon--green';
      case 'STATUS_CHANGED':        return 'dmg-metric-icon--blue';
      case 'CLASSIFICATION_CHANGED': return 'dmg-metric-icon--orange';
      case 'ASSIGNED':              return 'dmg-metric-icon--purple';
      case 'NOTE_ADDED':            return 'dmg-metric-icon--yellow';
      case 'AMOUNT_ADDED':
      case 'AMOUNT_STATUS_CHANGED':
      case 'AMOUNT_UPDATED':        return 'dmg-metric-icon--orange';
      case 'AMOUNT_DELETED':        return 'dmg-metric-icon--red';
      case 'CLOSED':                return 'dmg-metric-icon--green';
      default:                      return 'dmg-metric-icon--blue';
    }
  }

  // ─── IMPORTI ───

  /** Classe dmg-badge per lo stato di un importo (PROPOSED, APPROVED, CHARGED, CANCELLED...) */
  amountStatusBadgeClass(status?: string): string {
    switch ((status ?? '').toUpperCase()) {
      case 'PROPOSED':  return 'dmg-badge--pending';
      case 'APPROVED':
      case 'CHARGED':   return 'dmg-badge--resolved';
      case 'REJECTED':
      case 'CANCELLED': return 'dmg-badge--critical';
      default:          return 'dmg-badge--info';
    }
  }

  trackByEvent(index: number, ev: DamageEvent): number {
    return ev.damageEventId ?? index;
  }

  trackByAmount(index: number, amount: DamageAmount): number {
    return amount.damageAmountId ?? index;
  }
}
